import React, { useState } from 'react';
import { Info, Link as LinkIcon, LoaderCircle, Music2, Send } from 'lucide-react';
// react-icons 추가 (Facebook, Tiktok 추가됨)
import {
  FaInstagram,
  FaYoutube,
  FaXTwitter,
  FaDiscord,
  FaFacebook,
  FaTiktok,
  FaSpotify
} from 'react-icons/fa6';
import { getSavedLocale } from '../utils/locale';

const getImageUrl = (filename) => {
  if (!filename) return null;
  if (filename.startsWith('blob:')) return filename;
  return `/uploads/${filename}`;
};

const ensureAbsoluteUrl = (url) => {
  if (!url || typeof url !== 'string') return '#';
  const trimmedUrl = url.trim();
  if (!trimmedUrl) return '#';
  if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
    return trimmedUrl;
  }
  return `https://${trimmedUrl}`;
};

const getMusicEmbed = (rawUrl) => {
  try {
    const parsed = new URL(ensureAbsoluteUrl(rawUrl));
    const host = parsed.hostname.replace(/^www\./, '').toLowerCase();

    if (host === 'youtu.be') {
      const id = parsed.pathname.split('/').filter(Boolean)[0];
      return id ? { provider: 'youtube', src: `https://www.youtube-nocookie.com/embed/${id}` } : null;
    }
    if (['youtube.com', 'music.youtube.com', 'm.youtube.com'].includes(host)) {
      const parts = parsed.pathname.split('/').filter(Boolean);
      const id = parsed.searchParams.get('v') || (['shorts', 'embed'].includes(parts[0]) ? parts[1] : '');
      return id ? { provider: 'youtube', src: `https://www.youtube-nocookie.com/embed/${id}` } : null;
    }
    if (host === 'open.spotify.com') {
      const parts = parsed.pathname.split('/').filter(Boolean);
      const offset = parts[0]?.startsWith('intl-') ? 1 : 0;
      const type = parts[offset];
      const id = parts[offset + 1];
      if (['track', 'album', 'playlist', 'episode', 'show'].includes(type) && id) {
        return {
          provider: 'spotify',
          src: `https://open.spotify.com/embed/${type}/${id}`,
          tall: ['album', 'playlist', 'show'].includes(type)
        };
      }
    }
  } catch (error) {
    return null;
  }
  return null;
};

const AIAnswerBlock = ({ uniqueId, title, variant = 'default', textColor, tx }) => {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const ask = async (event) => {
    event.preventDefault();
    const trimmed = question.trim();
    if (!trimmed || !uniqueId || isLoading) return;

    setIsLoading(true);
    setAnswer('');
    setError('');
    try {
      const response = await fetch(`/api/profile/${encodeURIComponent(uniqueId)}/ai-answer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: trimmed })
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || tx('답변을 불러오지 못했습니다.', 'Khong the tai cau tra loi.'));
      }
      if (!response.body) throw new Error(tx('답변을 불러오지 못했습니다.', 'Khong the tai cau tra loi.'));
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let received = '';
      while (true) {
        const { value, done } = await reader.read();
        received += decoder.decode(value || new Uint8Array(), { stream: !done });
        setAnswer(received);
        if (done) break;
      }
    } catch (requestError) {
      setError(requestError.message || tx('잠시 후 다시 시도해 주세요.', 'Vui long thu lai sau.'));
    } finally {
      setIsLoading(false);
    }
  };

  const blockTitle = title || tx('AI에게 물어보기', 'Hoi AI');
  const historyNotice = (
    <span className="group relative inline-flex align-middle">
      <Info className="h-3.5 w-3.5 cursor-help text-gray-400" aria-label="질문과 답변 기록 안내" />
      <span className="pointer-events-none absolute left-1/2 top-full z-20 mt-2 hidden w-52 -translate-x-1/2 rounded-md bg-gray-950 px-3 py-2 text-center text-[11px] font-normal leading-4 text-white shadow-xl group-hover:block">
        질문과 답변은 프로필 운영자에게 표시될 수 있습니다.
      </span>
    </span>
  );
  const questionInput = (inputClassName, buttonClassName) => (
    <>
      <input
        value={question}
        onChange={(event) => setQuestion(event.target.value.slice(0, 300))}
        maxLength={300}
        placeholder={tx('궁금한 것을 물어보세요', 'Dat cau hoi cua ban')}
        aria-label={tx('AI에게 질문', 'Hoi AI')}
        className={inputClassName}
      />
      <button
        type="submit"
        disabled={!question.trim() || isLoading}
        aria-label={tx('질문 보내기', 'Gui cau hoi')}
        className={buttonClassName}
      >
        {isLoading ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
      </button>
    </>
  );

  if (variant === 'discord') {
    return (
      <section className="w-full overflow-hidden rounded-lg border border-[#1e1f22] bg-[#313338] text-left text-[#dbdee1] shadow-sm">
        <div className="border-b border-[#1e1f22] px-4 py-3">
          <div className="flex items-center gap-1.5 text-sm font-bold text-white" style={textColor ? { color: textColor } : undefined}>{blockTitle}{historyNotice}</div>
          <div className="mt-0.5 text-[11px] text-[#949ba4]">Discord DM</div>
        </div>
        {(answer || error) && (
          <div className="px-4 pt-4">
            <div className="flex items-baseline gap-2">
              <span className="text-sm font-semibold text-white">{blockTitle}</span>
              <span className="text-[10px] text-[#949ba4]">방금</span>
            </div>
            <p className={`mt-1 whitespace-pre-wrap text-sm leading-6 ${error ? 'text-[#f23f42]' : 'text-[#dbdee1]'}`} style={!error && textColor ? { color: textColor } : undefined}>
              {error || answer}
            </p>
          </div>
        )}
        <form onSubmit={ask} className="flex gap-2 p-3 pt-4">
          {questionInput(
            'min-w-0 flex-1 rounded-lg border-0 bg-[#383a40] px-3 py-2.5 text-sm text-[#dbdee1] outline-none placeholder:text-[#949ba4] focus:ring-2 focus:ring-[#5865f2]/60',
            'flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-[#5865f2] text-white transition-colors hover:bg-[#4752c4] disabled:cursor-not-allowed disabled:bg-[#4e5058]'
          )}
        </form>
      </section>
    );
  }

  if (variant === 'bubble') {
    return (
      <section className="w-full text-left">
        <div className="mb-2 flex items-center gap-1.5 px-1 text-sm font-bold text-gray-900" style={textColor ? { color: textColor } : undefined}>{blockTitle}{historyNotice}</div>
        {(answer || error) && (
          <div style={!error && textColor ? { color: textColor } : undefined} className={`relative mb-3 max-w-[88%] whitespace-pre-wrap rounded-2xl rounded-bl-sm px-4 py-3 text-sm leading-6 shadow-sm ${error
            ? 'bg-red-50 text-red-600'
            : 'bg-white text-gray-800'
            }`}>
            {error || answer}
          </div>
        )}
        <form onSubmit={ask} className="flex gap-2 rounded-2xl border border-[#dfe1e8] bg-white p-2 shadow-sm">
          {questionInput(
            'min-w-0 flex-1 border-0 bg-transparent px-2 py-2 text-sm text-gray-900 outline-none placeholder:text-gray-400',
            'flex h-9 w-9 flex-none items-center justify-center rounded-full bg-[#5865f2] text-white transition-colors hover:bg-[#4a56d1] disabled:cursor-not-allowed disabled:bg-gray-300'
          )}
        </form>
      </section>
    );
  }

  return (
    <section className="w-full overflow-hidden rounded-lg border border-[#dfe1e8] bg-white text-left shadow-sm">
      <div className="flex items-center gap-1.5 border-b border-[#e5e7ec] px-4 py-3 text-sm font-bold text-gray-900" style={textColor ? { color: textColor } : undefined}>
        {blockTitle}{historyNotice}
      </div>
      <form onSubmit={ask} className="flex gap-2 p-3">
        {questionInput(
          'min-w-0 flex-1 rounded-lg border border-[#d7d9e0] bg-[#f5f6f8] px-3 py-2.5 text-sm text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-[#5865F2] focus:ring-2 focus:ring-[#5865F2]/15',
          'flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-[#5865F2] text-white transition-colors hover:bg-[#4a56d1] disabled:cursor-not-allowed disabled:bg-gray-300'
        )}
      </form>
      {(answer || error) && (
        <div style={!error && textColor ? { color: textColor } : undefined} className={`mx-3 mb-3 whitespace-pre-wrap rounded-lg px-3 py-3 text-sm leading-6 ${error ? 'bg-red-50 text-red-600' : 'bg-[#f1f2ff] text-gray-800'}`}>
          {error || answer}
        </div>
      )}
    </section>
  );
};

const ProfilePreview = ({ profile, onShareClick, locale, displayMode = 'preview' }) => {
  if (!profile) return null;
  const activeLocale = locale || getSavedLocale();
  const isVi = activeLocale === 'vi';
  const tx = (ko, vi) => (isVi ? vi : ko);

  const {
    unique_id,
    layout_style = 'style1',
    background_color = '#FFFFFF',
    background_image_url,
    profile_image_url,
    display_name = tx('대표 문구', 'Thong diep chinh'),
    description = tx('상세 문구 (소개글)', 'Mo ta ngan'),
    display_name_color = '#111827',
    description_color = '#4B5563',
    font_size = 'medium',
    button_shape = 'rounded',
    button_color = '#6366F1',
    blocks = [],
    links = []
  } = profile;

  const dataToRender = (blocks && blocks.length > 0) ? blocks : links;

  const displayedProfileImageUrl = getImageUrl(profile_image_url);
  const displayedBackgroundImageUrl = getImageUrl(background_image_url);

  const fontSizeClasses = {
    small: 'text-sm',
    medium: 'text-base',
    large: 'text-lg',
  }[font_size] || 'text-base';

  const buttonShapeClasses = {
    sharp: 'rounded-none',
    rounded: 'rounded-lg',
    pill: 'rounded-full',
  }[button_shape] || 'rounded-lg';

  // [신규] SNS 플랫폼별 아이콘 매핑
  const snsIcons = {
    instagram: <FaInstagram size={24} />,
    x: <FaXTwitter size={20} />,
    youtube: <FaYoutube size={24} />,
    discord: <FaDiscord size={24} />,
    facebook: <FaFacebook size={24} />,
    tiktok: <FaTiktok size={24} />
  };

  // --- 블록 렌더링 ---
  const isPage = displayMode === 'page';

  const renderBlocks = () => (
    <div className="mt-4 flex w-full max-w-md flex-col gap-3 px-4 pb-10">
      {dataToRender.map((block, index) => {
        const blockType = block.type || 'link';

        const blockSizeClass = {
          small: 'py-3 text-sm',
          medium: 'py-4 text-base',
          large: 'py-5 text-lg'
        }[block.size || 'medium'];

        const textBlockSizeClass = {
          small: 'text-sm',
          medium: 'text-base',
          large: 'text-xl font-semibold'
        }[block.size || 'medium'];

        const blockButtonShapeClasses = {
          sharp: 'rounded-none',
          rounded: 'rounded-lg',
          pill: 'rounded-full'
        }[block.shape] || buttonShapeClasses;

        const buttonAlignmentClasses = {
          left: 'justify-start pl-11 pr-4 text-left',
          center: 'justify-center px-11 text-center',
          right: 'justify-end pl-11 pr-4 text-right'
        }[block.align || 'center'];

        const commonButtonStyle = `block w-full font-semibold ${blockSizeClass} ${blockButtonShapeClasses} transition-transform transform hover:scale-[1.02] shadow-md active:scale-95 flex items-center gap-2 relative overflow-hidden ${buttonAlignmentClasses}`;

        // 1. 링크 블록
        if (blockType === 'link') {
          if (!block.url && !block.title) return null;

          return (
            <a
              key={block.id || index}
              href={ensureAbsoluteUrl(block.url)}
              target="_blank"
              rel="noopener noreferrer"
              className={commonButtonStyle}
              style={{
                backgroundColor: block.buttonColor || button_color,
                color: block.textColor || '#ffffff'
              }}
            >
              <LinkIcon className="w-4 h-4 absolute left-4 opacity-70" />
              <span>{block.title || block.url || tx('링크', 'Link')}</span>
            </a>
          );
        }

        // 2. 텍스트 블록
        if (blockType === 'text') {
          return (
            <div
              key={block.id || index}
              className={`w-full p-2 whitespace-pre-wrap ${textBlockSizeClass}`}
              style={{
                color: block.textColor || '#1f2937',
                textAlign: ['left', 'center', 'right'].includes(block.align) ? block.align : 'center',
                textShadow: '0 1px 2px rgba(0,0,0,0.1)'
              }}
            >
              {block.content || tx('텍스트 내용', 'Noi dung van ban')}
            </div>
          );
        }

        // 3. SNS 블록 [수정됨: items 배열 순회 방식]
        if (blockType === 'sns') {
          // items가 있으면 사용, 없으면(구형 데이터) 기존 필드로 배열 생성 시도
          let itemsToRender = block.items || [];

          // 구형 데이터 호환성 (items가 비어있고 구형 필드가 있는 경우)
          if (itemsToRender.length === 0) {
             if (block.instagram_url) itemsToRender.push({ platform: 'instagram', url: block.instagram_url });
             if (block.x_url) itemsToRender.push({ platform: 'x', url: block.x_url });
             if (block.youtube_url) itemsToRender.push({ platform: 'youtube', url: block.youtube_url });
             if (block.discord_url) itemsToRender.push({ platform: 'discord', url: block.discord_url });
          }

          if (itemsToRender.length === 0) return null;

          return (
            <div key={block.id || index} className="w-full flex flex-wrap justify-center items-center gap-4 p-2 text-base">
              {itemsToRender.map((item, idx) => {
                const icon = snsIcons[item.platform];
                if (!icon || !item.url) return null;

                return (
                  <a
                    key={item.id || idx}
                    href={ensureAbsoluteUrl(item.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 bg-transparent rounded-full border border-gray-400 text-gray-800 hover:bg-gray-100 transition-transform hover:scale-110 flex items-center justify-center"
                  >
                    {icon}
                  </a>
                );
              })}
            </div>
          );
        }

        if (blockType === 'music') {
          const embed = getMusicEmbed(block.url);
          if (!embed) return null;
          return (
            <section key={block.id || index} className="w-full overflow-hidden rounded-lg border border-[#dfe1e8] bg-white shadow-sm">
              {block.title && (
                <div className="flex items-center gap-2 border-b border-[#e5e7ec] px-4 py-3 text-left text-sm font-bold text-gray-900" style={block.textColor ? { color: block.textColor } : undefined}>
                  {embed.provider === 'spotify' ? <FaSpotify className="h-4 w-4 text-[#1DB954]" /> : <Music2 className="h-4 w-4 text-[#5865F2]" />}
                  <span className="min-w-0 truncate">{block.title}</span>
                </div>
              )}
              <iframe
                src={embed.src}
                title={block.title || tx('음악', 'Am nhac')}
                loading="lazy"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                className={`block w-full border-0 ${embed.provider === 'youtube' ? 'aspect-video' : embed.tall ? 'h-[352px]' : 'h-[152px]'}`}
              />
            </section>
          );
        }

        if (blockType === 'ai') {
          return <AIAnswerBlock key={block.id || index} uniqueId={unique_id} title={block.title} variant={block.variant} textColor={block.textColor} tx={tx} />;
        }

        return null;
      })}
    </div>
  );

  const ShareButton = () => (
    <div className="absolute top-0 right-0 w-full p-5 flex justify-end items-center z-30 pointer-events-none">
      <button
        type="button"
        onClick={onShareClick}
        className="pointer-events-auto p-2 rounded-full bg-white/80 backdrop-blur-sm shadow-sm hover:bg-white transition-colors text-gray-700"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
        </svg>
      </button>
    </div>
  );

  // --- 레이아웃 1 ---
  if (layout_style === 'style1') {
    const topSectionHeightPx = '280px';
    return (
      <div
        className={`relative flex w-full flex-col ${isPage ? 'min-h-[100dvh]' : 'h-full overflow-y-auto scrollbar-hide'}`}
        style={{ backgroundColor: background_color }}
      >
        <div className="absolute top-0 left-0 w-full z-0" style={{ height: topSectionHeightPx }}>
          {displayedProfileImageUrl ? (
            <>
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${displayedProfileImageUrl})` }}
              />
              <div
                className="absolute inset-0"
                style={{ background: `linear-gradient(to bottom, rgba(0,0,0,0.1), transparent 58%, ${background_color})` }}
              />
            </>
          ) : (
            <div
              className="w-full h-full"
              style={{ backgroundColor: background_color }}
            />
          )}
        </div>
        <ShareButton />
        <div
          className={`relative z-10 flex flex-col items-center text-center ${isPage ? 'min-h-[100dvh]' : 'min-h-full'}`}
          style={{ paddingTop: topSectionHeightPx }}
        >
          {!displayedProfileImageUrl && (
            <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center mb-4 shadow-md border-4 border-white -mt-12">
              <span className="text-3xl">👤</span>
            </div>
          )}
          <h1 className="text-2xl font-bold mb-2 text-gray-900 px-4" style={{ color: display_name_color }}>{display_name}</h1>
          <p className={`mb-6 text-gray-600 whitespace-pre-line px-4 ${fontSizeClasses}`} style={{ color: description_color }}>{description}</p>
          {renderBlocks()}
        </div>
      </div>
    );
  }

  // --- 레이아웃 2 ---
  if (layout_style === 'style2') {
    return (
      <div
        className={`relative flex w-full flex-col items-center p-6 ${isPage ? 'min-h-[100dvh]' : 'min-h-full'}`}
        style={{ backgroundColor: background_color }}
      >
        <ShareButton />
        <div className="mt-14 flex w-full max-w-md flex-1 flex-col items-center">
          {displayedProfileImageUrl ? (
            <img
              src={displayedProfileImageUrl}
              alt={tx("프로필", "Ho so")}
              className="w-28 h-28 rounded-full object-cover mb-4 shadow-lg border-4 border-white"
            />
          ) : (
            <div className="w-28 h-28 rounded-full bg-gray-300 flex items-center justify-center mb-4 shadow-lg border-4 border-white">
              <span className="text-4xl text-white">👤</span>
            </div>
          )}
          <h1 className="text-2xl font-bold mb-2 text-gray-900" style={{ color: display_name_color }}>{display_name}</h1>
          <p className={`mb-8 text-gray-700 text-center whitespace-pre-line ${fontSizeClasses}`} style={{ color: description_color }}>{description}</p>
          {renderBlocks()}
        </div>
      </div>
    );
  }

  // --- 레이아웃 4 ---
  if (layout_style === 'style4') {
    return (
      <div
        className={`flex w-full flex-col bg-gray-50 ${isPage ? 'min-h-[100dvh]' : 'min-h-full'}`}
        style={{ backgroundColor: background_color }}
      >
        <div
          className="relative aspect-[16/9] w-full flex-shrink-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${displayedBackgroundImageUrl || 'https://placehold.co/600x400/e2e8f0/94a3b8?text=Background'})`
          }}
        >
          <ShareButton />
        </div>
        <div className="relative -mt-12 flex w-full flex-1 flex-col items-center px-6">
          {displayedProfileImageUrl ? (
            <img
              src={displayedProfileImageUrl}
              alt={tx("프로필", "Ho so")}
              className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-md mb-3"
            />
          ) : (
            <div className="w-24 h-24 rounded-full bg-gray-300 flex items-center justify-center border-4 border-white shadow-md mb-3">
              <span className="text-2xl">👤</span>
            </div>
          )}
          <h1 className="text-2xl font-bold mb-1 text-gray-900" style={{ color: display_name_color }}>{display_name}</h1>
          <p className={`mb-6 text-gray-600 text-center whitespace-pre-line ${fontSizeClasses}`} style={{ color: description_color }}>{description}</p>
          {renderBlocks()}
        </div>
      </div>
    );
  }

  return <div>{tx("유효하지 않은 레이아웃입니다.", "Bo cuc khong hop le.")}</div>;
};

export default ProfilePreview;
